import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { getCustomers } from '@/features/customers/api';
import { formatPhoneDisplay, phoneSearchTerm } from '@/lib/phone';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  Users,
  UserPlus,
  ShieldCheck,
  Clock,
  Inbox,
  AlertTriangle,
  Star,
  RefreshCw,
} from '@/lib/icons';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ListFilterBar, NoFilterResults } from '@/components/filters';
import type { FilterChip } from '@/components/filters';
import { useListFilters } from '@/hooks/use-list-filters';
import { ApiErrorCard } from '@/components/feedback/ApiErrorCard';
import { useAuth } from '@/hooks/useAuth';
import { formatDate } from '@/lib/dates';

/**
 * `submitted` is not a `KYCStatus` label — it is the server-side review queue:
 * customers who sent identity details from the app and are waiting on a
 * decision, oldest first. It exists as a filter because verification is our
 * job now, and a reviewer needs a way to find the work rather than paging
 * through everyone the enum happens to call `pending`.
 */
const KYC_VALUES = ['submitted', 'verified', 'pending', 'rejected'] as const;

const customersSearchSchema = z.object({
  q: z.string().optional(),
  branchId: z.string().optional(),
  status: z.enum(KYC_VALUES).optional(),
  page: z.coerce.number().int().min(1).optional(),
});

export const Route = createFileRoute('/_layout/customers/')({
  validateSearch: customersSearchSchema,
  component: CustomersPage,
});

/**
 * Takes the row rather than the bare status, because `pending` means two
 * different things and only one of them is work: a customer who has never sent
 * us anything, and one whose documents have been sitting in our queue since
 * Tuesday. Rendering both as the same amber "Pending" is how a review queue
 * quietly ages.
 */
const kycBadge = (c: { kycStatus: string; kycSubmittedAt: string | null }) => {
  switch (c.kycStatus) {
    case 'verified':
      return (
        <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30 dark:text-emerald-400">
          <ShieldCheck className="w-3 h-3 mr-1" />
          Verified
        </Badge>
      );
    case 'pending':
      return c.kycSubmittedAt ? (
        <Badge className="bg-blue-500/15 text-blue-600 border-blue-500/30 dark:text-blue-400">
          <Inbox className="w-3 h-3 mr-1" />
          Awaiting review
        </Badge>
      ) : (
        <Badge className="bg-amber-500/15 text-amber-600 border-amber-500/30 dark:text-amber-400">
          <Clock className="w-3 h-3 mr-1" />
          Not submitted
        </Badge>
      );
    case 'rejected':
      return (
        <Badge className="bg-red-500/15 text-red-600 border-red-500/30 dark:text-red-400">
          <AlertTriangle className="w-3 h-3 mr-1" />
          Rejected
        </Badge>
      );
    default:
      return <Badge variant="outline">{c.kycStatus}</Badge>;
  }
};

const tierBadge = (tier: string | null) => {
  if (!tier) return <span className="text-muted-foreground text-xs">--</span>;
  const colors: Record<string, string> = {
    A: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30 dark:text-emerald-400',
    B: 'bg-blue-500/15 text-blue-600 border-blue-500/30 dark:text-blue-400',
    C: 'bg-amber-500/15 text-amber-600 border-amber-500/30 dark:text-amber-400',
    D: 'bg-orange-500/15 text-orange-600 border-orange-500/30 dark:text-orange-400',
    E: 'bg-red-500/15 text-red-600 border-red-500/30 dark:text-red-400',
  };
  return (
    <Badge className={colors[tier] || ''}>
      <Star className="w-3 h-3 mr-1" />
      Tier {tier}
    </Badge>
  );
};

const avatar = (name: string) => {
  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const colors = [
    'bg-blue-500',
    'bg-emerald-500',
    'bg-violet-500',
    'bg-amber-500',
    'bg-rose-500',
    'bg-teal-500',
  ];
  const idx = name.charCodeAt(0) % colors.length;
  return (
    <div
      className={`w-9 h-9 rounded-full ${colors[idx]} flex items-center justify-center text-white text-xs font-bold shrink-0`}
    >
      {initials}
    </div>
  );
};

const PAGE_SIZE = 20;

function CustomersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // `/customers/new` is route-guarded on customers:create.
  const canCreate = useAuth().hasPermission('customers', 'create');
  const search = Route.useSearch();

  const {
    searchInput,
    setSearchInput,
    debouncedSearch,
    isSearchPending,
    setFilters,
    reset,
    activeCount,
  } = useListFilters({ search, to: '/customers' });

  const page = search.page ?? 1;

  /**
   * This box searches names, ids and phones alike, so it stays free text — but
   * when what was typed *is* a phone number, search its subscriber digits
   * instead of the literal string. The backend matches `%term%` against a
   * `phone` column that holds a mix of `612345678`, `0612345678` and
   * `+252612345678`, so pasting a full E.164 number would miss every row that
   * predates that convention. The national part is a substring of all of them.
   */
  const phoneTerm = phoneSearchTerm(debouncedSearch);

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['customers', phoneTerm, search.status, search.branchId],
    queryFn: () =>
      getCustomers({
        search: phoneTerm,
        kycStatus: search.status,
        branchId: search.branchId,
      }),
    // Global default is staleTime=5min, refetchOnWindowFocus=false — fine
    // for static reference data but stale for a list that agents return
    // to after registering / editing customers elsewhere. Always refetch
    // on mount so navigating back to /customers shows up-to-date rows.
    // The Refresh button covers the other case: sitting on the page while
    // a colleague registers someone.
    refetchOnMount: 'always',
    placeholderData: (previous) => previous,
  });

  const allCustomers = data?.value ?? [];
  const totalPages = Math.max(1, Math.ceil(allCustomers.length / PAGE_SIZE));
  const customers = allCustomers.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );
  const startItem = allCustomers.length > 0 ? (page - 1) * PAGE_SIZE + 1 : 0;
  const endItem = Math.min(page * PAGE_SIZE, allCustomers.length);

  const verified = allCustomers.filter(
    (c) => c.kycStatus === 'verified'
  ).length;
  // Work on our desk, not customers who have yet to start — the two are the
  // same enum label and only one of them is something anyone can act on.
  const awaitingReview = allCustomers.filter(
    (c) => c.kycStatus !== 'verified' && c.kycSubmittedAt != null
  ).length;
  const totalContracts = allCustomers.reduce(
    (s, c) => s + c.activeContracts,
    0
  );

  const stats = [
    {
      label: 'Total Customers',
      value: allCustomers.length,
      icon: Users,
      color: 'from-blue-500 to-blue-600',
    },
    {
      label: 'KYC Verified',
      value: verified,
      icon: ShieldCheck,
      color: 'from-emerald-500 to-emerald-600',
    },
    {
      label: 'Awaiting Review',
      value: awaitingReview,
      icon: Inbox,
      color: 'from-blue-500 to-blue-600',
    },
    {
      label: 'Active Contracts',
      value: totalContracts,
      icon: Star,
      color: 'from-violet-500 to-violet-600',
    },
  ];

  // No counts: the customers endpoint has no count-by-status companion, and a
  // count derived from the already-filtered response would only ever report
  // the bucket you are standing in.
  const chips: ReadonlyArray<FilterChip<'all' | (typeof KYC_VALUES)[number]>> = [
    { value: 'all', label: 'All' },
    // First after All, because it is the only bucket that is a to-do list.
    {
      value: 'submitted',
      label: 'Awaiting review',
      icon: <Inbox className="h-3 w-3" />,
    },
    { value: 'verified', label: 'Verified', icon: <ShieldCheck className="h-3 w-3" /> },
    { value: 'pending', label: 'Pending', icon: <Clock className="h-3 w-3" /> },
    { value: 'rejected', label: 'Rejected', icon: <AlertTriangle className="h-3 w-3" /> },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Customers</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage customer profiles, KYC verification, and credit tiers
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => refetch()}
            disabled={isFetching}
            aria-label="Refresh customer list"
          >
            <RefreshCw
              className={`w-4 h-4 mr-2 ${isFetching ? 'animate-spin' : ''}`}
            />
            {isFetching ? 'Refreshing…' : 'Refresh'}
          </Button>
          {canCreate && (
            <Button
              className="bg-gradient-to-r from-primary to-primary/85 hover:from-primary/90 hover:to-primary/75 text-primary-foreground shadow-md hover:shadow-lg transition-all duration-200"
              onClick={() => navigate({ to: '/customers/new' })}
            >
              <UserPlus className="w-4 h-4 mr-2" /> Add Customer
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 animate-stagger-in">
        {stats.map((s) => (
          <Card
            key={s.label}
            className="relative overflow-hidden border-0 shadow-md"
          >
            <div
              className={`absolute inset-0 bg-gradient-to-br ${s.color} opacity-[0.07]`}
            />
            <CardContent className="p-4 flex items-center gap-3 relative">
              <div
                className={`p-2.5 rounded-xl bg-gradient-to-br ${s.color} text-white shadow-lg`}
              >
                <s.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {isLoading ? '—' : s.value}
                </p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <ListFilterBar
        chips={chips}
        chipValue={search.status ?? 'all'}
        onChipChange={(value) =>
          setFilters({ status: value === 'all' ? undefined : value })
        }
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder={t('filters.searchPlaceholder.customers')}
        isSearchPending={isSearchPending}
        branchId={search.branchId}
        onBranchChange={(branchId) => setFilters({ branchId })}
        activeCount={activeCount}
        onReset={reset}
        isPending={isFetching}
      />

      {/* Table */}
      {isLoading ? (
        <Card className="shadow-md border-0">
          <CardContent className="p-6 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </CardContent>
        </Card>
      ) : isError ? (
        <ApiErrorCard
          error={error}
          onRetry={() => refetch()}
          title="Failed to load customers"
        />
      ) : customers.length === 0 ? (
        <NoFilterResults
          isFiltered={activeCount > 0}
          emptyTitle="No customers yet"
          emptyDescription="Register the first customer to start taking applications."
          icon={Users}
          onReset={reset}
          action={
            canCreate ? (
              <Button onClick={() => navigate({ to: '/customers/new' })}>
                <UserPlus className="w-4 h-4 mr-2" /> Add Customer
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Card className="shadow-md border-0">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40">
                    <TableHead>Customer</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>KYC Status</TableHead>
                    <TableHead>Credit Tier</TableHead>
                    <TableHead className="text-center">Contracts</TableHead>
                    <TableHead>Joined</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customers.map((c) => (
                    <TableRow
                      key={c.id}
                      className="hover:bg-muted/30 transition-colors cursor-pointer row-hover-lift"
                      onClick={() =>
                        navigate({
                          to: '/customers/$customerId',
                          params: { customerId: c.id },
                        })
                      }
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {avatar(`${c.firstName} ${c.lastName}`)}
                          <span className="font-medium">{`${c.firstName} ${c.lastName}`}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground font-mono text-sm">
                        {formatPhoneDisplay(c.phoneNumber)}
                      </TableCell>
                      <TableCell>{kycBadge(c)}</TableCell>
                      <TableCell>{tierBadge(c.creditTier)}</TableCell>
                      <TableCell className="text-center font-semibold">
                        {c.activeContracts}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatDate(c.createdAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {allCustomers.length > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {startItem}-{endItem} of {allCustomers.length}
          </p>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => setFilters({ page: Math.max(1, page - 1) })}
                  className={
                    page <= 1
                      ? 'pointer-events-none opacity-50'
                      : 'cursor-pointer'
                  }
                />
              </PaginationItem>
              {Array.from({ length: totalPages }).map((_, i) => (
                <PaginationItem key={i}>
                  <PaginationLink
                    isActive={page === i + 1}
                    onClick={() => setFilters({ page: i + 1 })}
                    className="cursor-pointer"
                  >
                    {i + 1}
                  </PaginationLink>
                </PaginationItem>
              ))}
              <PaginationItem>
                <PaginationNext
                  onClick={() => setFilters({ page: Math.min(totalPages, page + 1) })}
                  className={
                    page >= totalPages
                      ? 'pointer-events-none opacity-50'
                      : 'cursor-pointer'
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
}
